import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath, OnboardingFlowMode } from './onboardingRoutes';
import { launchSuiteApp } from '../../utils/launchSuiteApp';
import { AppInfoTooltip } from '../../components/AppInfoTooltip';

/**
 * CompletePage — the Workspace Assembly experience (PR-A2).
 *
 * The final onboarding step is a state machine that turns the user's
 * selected outcomes into a real, provisioned workspace. It reflects only
 * REAL provisioning signals — there are no faked phases and no pretend
 * workflow/automation sections (the backend has no data for those yet).
 *
 * Phases:
 *  loading           — fetching onboarding state
 *  resolving         — fetching the resolved plan (pricing-summary)
 *  ready-to-assemble — plan shown; user confirms
 *  provisioning      — POST /api/onboarding/complete in flight
 *  verifying         — GET /api/workspace/current confirming the result
 *  ready             — workspace provisioned; launcher shown
 *  degraded          — workspace not yet reflecting apps; retry
 *  failed-recoverable— provisioning failed; retry
 *
 * Backend step machine and ONBOARDING_FLOWS are unchanged — this is a
 * frontend reshape of the existing `complete` step.
 */

type AssemblyPhase =
  | 'loading'
  | 'resolving'
  | 'ready-to-assemble'
  | 'provisioning'
  | 'verifying'
  | 'ready'
  | 'degraded'
  | 'failed-recoverable';

interface PlanOutcome {
  code: string;
  displayName: string;
}

interface PlanApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c PR-C1 — Application Registry v2 copy, mode-resolved by
   * /api/onboarding/pricing-summary (PR-B). Optional so legacy payloads
   * degrade to displayName-only via the AppInfoTooltip fallback.
   */
  subtitle?: string;
  description?: string;
  /**
   * Phase 2.x-c PR-C3b — accepted on the type so the pricing-summary
   * payload passes through cleanly. Complete.tsx does NOT render
   * traceability (out of PR-C3b scope; provisioning/launch stays
   * price-free). If a later PR wants to show "from {outcome}" here,
   * the data is already available.
   */
  activatedByOutcomes?: string[];
}

interface LaunchableApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c PR-C1 — Application Registry v2 copy, mode-resolved by
   * /api/workspace/current (PR-A). Same optional-with-fallback contract
   * as PlanApp.
   */
  subtitle?: string;
  description?: string;
  launchUrl: string | null;
  launchable: boolean;
  /** H-2A: true → launch via the OIDC flow; false/absent → bare link. */
  oidcLaunch?: boolean;
}

const MODE_COPY: Record<OnboardingFlowMode, {
  assembleHeading: string;
  assembleSub: string;
  workingHeading: string;
  workingSub: string;
  readyHeading: string;
  readySub: string;
  confirmCta: string;
}> = {
  business: {
    assembleHeading: 'Assemble your business workspace',
    assembleSub:
      "Here's the operating system we'll switch on for your clients, operations, growth, and team execution.",
    workingHeading: 'Activating your business workspace',
    workingSub: 'Switching on your apps and getting your workspace ready…',
    readyHeading: 'Your business workspace is ready',
    readySub: 'Everything you selected is now switched on for your team.',
    confirmCta: 'Assemble my workspace',
  },
  personal: {
    assembleHeading: 'Set up your space',
    assembleSub:
      "Here's everything we'll set up to help you organize your home, goals, and life.",
    workingHeading: 'Setting up your space',
    workingSub: 'Getting your apps and your space ready…',
    readyHeading: 'Your space is ready',
    readySub: 'Everything you chose is set up and ready for you.',
    confirmCta: 'Set up my space',
  },
};

/** Only OIDC callback URLs are honored as post-completion redirects. */
function isValidOidcContinueUrl(url: string): boolean {
  return url.startsWith('/api/oidc/');
}

export function CompletePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  const [phase, setPhase] = useState<AssemblyPhase>('loading');
  const [mode, setMode] = useState<OnboardingFlowMode | null>(null);
  const [workspaceType, setWorkspaceType] =
    useState<'personal' | 'team' | 'enterprise' | null>(null);
  const [outcomes, setOutcomes] = useState<PlanOutcome[]>([]);
  const [planApps, setPlanApps] = useState<PlanApp[]>([]);
  const [trialDays, setTrialDays] = useState(0);
  const [workspaceApps, setWorkspaceApps] = useState<LaunchableApp[]>([]);
  const [defaultApp, setDefaultApp] = useState<LaunchableApp | null>(null);
  const [error, setError] = useState('');

  /** Verify the provisioned workspace via the canonical /api/workspace/current. */
  const verifyWorkspace = useCallback(async () => {
    setPhase('verifying');
    setError('');
    try {
      const res = await fetch('/api/workspace/current', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setPhase('degraded');
        return;
      }
      if (data.workspace?.mode === 'business' || data.workspace?.mode === 'personal') {
        setMode(data.workspace.mode);
      }
      if (data.status === 'provisioned') {
        // Preserve subtitle + description from /api/workspace/current
        // (already mode-resolved by PR-A). AppInfoTooltip handles the
        // absence of either field cleanly.
        const rawApps = Array.isArray(data.apps) ? data.apps : [];
        setWorkspaceApps(
          rawApps.map((a: LaunchableApp) => ({
            appId: a.appId,
            displayName: a.displayName,
            subtitle: a.subtitle,
            description: a.description,
            launchUrl: a.launchUrl,
            launchable: a.launchable,
            oidcLaunch: a.oidcLaunch,
          })),
        );
        setDefaultApp(data.defaultApp ?? null);
        setPhase('ready');
      } else {
        // `empty` (no apps yet), or any other non-provisioned status — the
        // workspace exists but is not ready to launch. Recoverable.
        setPhase('degraded');
      }
    } catch {
      setPhase('degraded');
    }
  }, []);

  /** Load the resolved plan (outcomes + apps) for the pre-provision preview. */
  const loadPlan = useCallback(async () => {
    setPhase('resolving');
    setError('');
    try {
      const res = await fetch('/api/onboarding/pricing-summary', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setError("We couldn't prepare your workspace. Your selections are safe — please try again.");
        setPhase('failed-recoverable');
        return;
      }
      const planMode: OnboardingFlowMode = data.mode === 'personal' ? 'personal' : 'business';
      setMode(planMode);

      // No usable outcomes — never show an empty assembly. Recover to the
      // outcomes step (R3 missing-outcomes contract).
      if (data.status === 'missing-outcomes') {
        navigate(onboardingPath(planMode, 'outcomes', continueUrl));
        return;
      }

      setOutcomes(
        (data.selectedOutcomes ?? []).map((o: PlanOutcome) => ({
          code: o.code,
          displayName: o.displayName,
        })),
      );
      // Preserve subtitle + description from /api/onboarding/pricing-summary
      // (mode-resolved by PR-B). The ready-to-assemble chip and the
      // provisioning "Activating your apps" list both consume this.
      setPlanApps(
        (data.apps ?? []).map((a: PlanApp) => ({
          appId: a.appId,
          displayName: a.displayName,
          subtitle: a.subtitle,
          description: a.description,
        })),
      );
      setTrialDays(data.pricing?.trialDays ?? 0);
      setPhase('ready-to-assemble');
    } catch {
      setError('Network error. Please try again.');
      setPhase('failed-recoverable');
    }
  }, [navigate, continueUrl]);

  // Mount: load onboarding state. An already-complete workspace skips
  // confirm/provision and goes straight to verification.
  useEffect(() => {
    async function loadInitial() {
      try {
        const res = await fetch('/api/onboarding/state', { credentials: 'include' });
        const data = await res.json();
        if (!res.ok || !data?.state) {
          setError("We couldn't load your workspace. Please try again.");
          setPhase('failed-recoverable');
          return;
        }
        const st = data.state;
        if (st.onboardingMode === 'business' || st.onboardingMode === 'personal') {
          setMode(st.onboardingMode);
        }
        if (st.workspaceType) setWorkspaceType(st.workspaceType);

        if (st.isComplete) {
          // Already provisioned — verify, never re-POST complete.
          await verifyWorkspace();
        } else {
          await loadPlan();
        }
      } catch {
        setError('Network error. Please try again.');
        setPhase('failed-recoverable');
      }
    }
    loadInitial();
  }, [verifyWorkspace, loadPlan]);

  /** Confirm → POST /api/onboarding/complete (the one real provisioning call). */
  const confirmAssembly = useCallback(async () => {
    setPhase('provisioning');
    setError('');
    try {
      const res = await fetch('/api/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        await verifyWorkspace();
        return;
      }
      // 409 ALREADY_COMPLETE is not a failure — the workspace is provisioned.
      if (res.status === 409 || data?.code === 'ALREADY_COMPLETE') {
        await verifyWorkspace();
        return;
      }
      if (data?.code === 'EMAIL_NOT_VERIFIED') {
        navigate('/auth/verify-email');
        return;
      }
      setError(data?.error || 'We could not finish assembling your workspace.');
      setPhase('failed-recoverable');
    } catch {
      setError('Network error. Please try again.');
      setPhase('failed-recoverable');
    }
  }, [navigate, verifyWorkspace]);

  const handleBack = async () => {
    try {
      await fetch('/api/onboarding/back', { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore errors on back navigation.
    }
    if (mode === 'personal') {
      navigate(onboardingPath('personal', 'pricing', continueUrl));
    } else if (workspaceType === 'personal') {
      navigate(onboardingPath('business', 'review', continueUrl));
    } else {
      navigate(onboardingPath('business', 'invites', continueUrl));
    }
  };

  const goHome = () => navigate('/home');

  const layoutMode: 'business' | 'personal' | null = mode;
  const copy = MODE_COPY[mode ?? 'business'];

  // ── Working states (loading / resolving / provisioning / verifying) ──
  if (phase === 'loading' || phase === 'resolving') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Preparing your workspace…</p>
        </div>
      </OnboardingLayout>
    );
  }

  if (phase === 'provisioning' || phase === 'verifying') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="bg-white shadow-lg rounded-lg p-8">
          <div className="text-center mb-6">
            <div className="animate-spin w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <h1 className="text-2xl font-semibold text-gray-900 mt-5">{copy.workingHeading}</h1>
            <p className="text-gray-500 mt-2">{copy.workingSub}</p>
          </div>
          {planApps.length > 0 && (
            <div className="max-w-md mx-auto">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Activating your apps
              </h2>
              {/*
                Phase 2.x-c PR-C1 — subtitle beneath displayName so the
                user sees the enduring value being switched on, not just
                a bare name. Description surfaces via AppInfoTooltip.
                Falls back to a plain list item when the app has no copy.
              */}
              <ul className="space-y-2">
                {planApps.map((app) => (
                  <li
                    key={app.appId}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                    <AppInfoTooltip
                      description={app.description}
                      className={`text-left leading-tight ${
                        app.description
                          ? 'rounded px-0.5 -mx-0.5 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400'
                          : ''
                      }`}
                      testId={`activating-app-${app.appId}`}
                    >
                      <span className="block font-medium text-gray-800">
                        {app.displayName}
                      </span>
                      {app.subtitle && (
                        <span className="block text-xs text-gray-500">
                          {app.subtitle}
                        </span>
                      )}
                    </AppInfoTooltip>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </OnboardingLayout>
    );
  }

  // ── Ready-to-assemble: the resolved plan + confirm ──
  if (phase === 'ready-to-assemble') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900">{copy.assembleHeading}</h1>
            <p className="text-gray-500 mt-2">{copy.assembleSub}</p>
          </div>

          <div className="max-w-xl mx-auto space-y-6">
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                What you're improving ({outcomes.length})
              </h2>
              <ul className="space-y-2">
                {outcomes.map((o) => (
                  <li key={o.code} className="flex items-center gap-2 text-sm text-gray-700">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {o.displayName}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Apps we're switching on ({planApps.length})
              </h2>
              {/*
                Phase 2.x-c PR-C1 — chip mirrors PricingSummary's layout
                so the last two onboarding screens read consistently.
              */}
              <div className="flex flex-wrap gap-2">
                {planApps.map((app) => (
                  <AppInfoTooltip
                    key={app.appId}
                    description={app.description}
                    className={`px-3 py-1 bg-gray-100 rounded-full text-xs text-left leading-tight ${
                      app.description
                        ? 'hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400'
                        : ''
                    }`}
                    testId={`assemble-app-${app.appId}`}
                  >
                    <span className="block font-medium text-gray-800">
                      {app.displayName}
                    </span>
                    {app.subtitle && (
                      <span className="block text-[10px] text-gray-500">
                        {app.subtitle}
                      </span>
                    )}
                  </AppInfoTooltip>
                ))}
              </div>
            </div>

            {trialDays > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  Your {trialDays}-day free trial starts as soon as your workspace is assembled.
                </p>
              </div>
            )}
          </div>

          <div className="max-w-xl mx-auto mt-8 pt-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
            >
              Back
            </button>
            <button
              type="button"
              onClick={confirmAssembly}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {copy.confirmCta}
            </button>
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  // ── Degraded: workspace exists but is not yet launchable ──
  if (phase === 'degraded') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">Your workspace is still settling</h1>
          <p className="text-sm text-gray-500 mt-2">
            Your workspace was created and your selections are saved. Its apps just need a moment
            to finish switching on.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row sm:justify-center gap-3">
            <button
              type="button"
              onClick={verifyWorkspace}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Check again
            </button>
            <button
              type="button"
              onClick={goHome}
              className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
            >
              Go to your workspace
            </button>
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  // ── Failed but recoverable: provisioning did not complete ──
  if (phase === 'failed-recoverable') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
          <p className="text-sm text-gray-500 mt-2">
            {error || "We couldn't finish assembling your workspace. Your selections are safe."}
          </p>
          <button
            type="button"
            onClick={confirmAssembly}
            className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  // ── Ready: provisioned workspace + launcher ──
  const oidcContinue = continueUrl && isValidOidcContinueUrl(continueUrl) ? continueUrl : '';
  const defaultLaunchUrl =
    defaultApp?.launchable && defaultApp.launchUrl ? defaultApp.launchUrl : '';
  const primaryLaunch = oidcContinue || defaultLaunchUrl;
  // H-2A: route the default-app CTA through the OIDC launch flow when the
  // app is handoff-enabled. The onboarding `?continue=` OIDC continuation
  // (oidcContinue) is a separate flow and is never touched here.
  const oidcLaunchAppId =
    !oidcContinue && defaultLaunchUrl && defaultApp?.oidcLaunch === true
      ? defaultApp.appId
      : null;

  return (
    <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
      <div className="bg-white shadow-lg rounded-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">{copy.readyHeading}</h1>
          <p className="text-gray-500 mt-2">{copy.readySub}</p>
        </div>

        {workspaceApps.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Your apps ({workspaceApps.length})
            </h2>
            {/*
              Phase 2.x-c PR-C1 — same chip layout as PricingSummary and
              the ready-to-assemble screen, so the completed-workspace
              view carries the enduring value into the launcher-adjacent
              summary.
            */}
            <div className="flex flex-wrap gap-2">
              {workspaceApps.map((app) => (
                <AppInfoTooltip
                  key={app.appId}
                  description={app.description}
                  className={`px-3 py-1 bg-white border border-gray-200 rounded-full text-xs text-left leading-tight ${
                    app.description
                      ? 'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                      : ''
                  }`}
                  testId={`your-app-${app.appId}`}
                >
                  <span className="block font-medium text-gray-800">
                    {app.displayName}
                  </span>
                  {app.subtitle && (
                    <span className="block text-[10px] text-gray-500">
                      {app.subtitle}
                    </span>
                  )}
                </AppInfoTooltip>
              ))}
            </div>
          </div>
        )}

        <div className="max-w-md mx-auto flex flex-col gap-3">
          {primaryLaunch ? (
            <a
              href={primaryLaunch}
              onClick={(e) => {
                if (!oidcLaunchAppId) return;
                e.preventDefault();
                void launchSuiteApp(oidcLaunchAppId, {
                  surface: 'assembly-ready',
                  fallbackUrl: defaultLaunchUrl,
                });
              }}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium text-center hover:bg-blue-700 transition-colors"
            >
              {oidcContinue
                ? 'Continue'
                : `Open ${defaultApp?.displayName ?? 'your workspace'}`}
            </a>
          ) : null}
          <button
            type="button"
            onClick={goHome}
            className={
              primaryLaunch
                ? 'px-8 py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium'
                : 'px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors'
            }
          >
            Go to your workspace
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
